import React, { useState } from 'react';
import { Star, User, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([
    {
      id: 1,
      name: "Rajesh Kumar",
      rating: 5,
      comment: "Excellent service! The driver was very professional and the car was clean. Highly recommend for Varanasi trips.",
      date: "2 weeks ago"
    },
    {
      id: 2,
      name: "Priya Sharma",
      rating: 5,
      comment: "Best taxi service in Varanasi! Booked an Innova for our family trip to Ayodhya. Very comfortable journey and reasonable rates.",
      date: "1 month ago"
    },
    {
      id: 3,
      name: "Amit Verma",
      rating: 4,
      comment: "Good service and punctual. Driver knew all the routes well. Will definitely use again for my next trip.",
      date: "3 weeks ago"
    },
    {
      id: 4,
      name: "Sunita Devi",
      rating: 5,
      comment: "Very reliable and safe. Used their tempo traveller for our group pilgrimage. Everyone was satisfied with the service.",
      date: "1 week ago"
    },
    {
      id: 5,
      name: "Vikash Singh",
      rating: 5,
      comment: "Professional drivers and well-maintained vehicles. Airport pickup was on time. Great experience!",
      date: "2 months ago"
    },
    {
      id: 6,
      name: "Kavita Mishra",
      rating: 4,
      comment: "Affordable rates and good service. The booking process was smooth. Recommend for local and outstation travel.",
      date: "3 weeks ago"
    }
  ]);

  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    comment: ''
  });

  const [hoveredRating, setHoveredRating] = useState(0);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newReview = {
      id: reviews.length + 1,
      ...formData,
      date: 'Just now'
    };
    setReviews([newReview, ...reviews]);
    toast.success('Thank you for your review!');
    setFormData({ name: '', rating: 5, comment: '' });
  };

  const renderStars = (rating, interactive = false, size = 'w-5 h-5') => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= (interactive ? hoveredRating || formData.rating : rating)
                ? 'fill-orange-500 text-orange-500'
                : 'text-gray-300'
            } ${interactive ? 'cursor-pointer transition-colors' : ''}`}
            onClick={() => interactive && setFormData({ ...formData, rating: star })}
            onMouseEnter={() => interactive && setHoveredRating(star)}
            onMouseLeave={() => interactive && setHoveredRating(0)}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            Customer <span className="text-orange-600">Reviews</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Read what our satisfied customers have to say about our services
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 max-w-6xl mx-auto">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-orange-100 p-3 rounded-full">
                  <User className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">{review.name}</h3>
                  <p className="text-xs text-gray-500">{review.date}</p>
                </div>
              </div>

              {/* Rating */}
              <div className="mb-3">
                {renderStars(review.rating)}
              </div>

              {/* Comment */}
              <p className="text-gray-700 text-sm leading-relaxed">{review.comment}</p>
            </div>
          ))}
        </div>

        {/* Write a Review Form */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-orange-100">
            <div className="flex items-center gap-3 mb-6">
              <MessageSquare className="w-6 h-6 text-orange-600" />
              <h3 className="text-2xl font-bold text-gray-900">Write a Review</h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your name"
                  required
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                />
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating
                </label>
                {renderStars(formData.rating, true, 'w-8 h-8')}
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  placeholder="Share your experience with us..."
                  rows="4"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                ></textarea>
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
              >
                Submit Review
              </Button>
            </form>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">5000+</div>
            <p className="text-gray-600 text-sm">Happy Customers</p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">4.9</div>
            <p className="text-gray-600 text-sm">Average Rating</p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">1000+</div>
            <p className="text-gray-600 text-sm">Reviews</p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">100%</div>
            <p className="text-gray-600 text-sm">Satisfaction</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;